import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONNECTOR_CATEGORIES,
  integrationApi,
  type ActivityEntry,
  type Connector,
  type ConnectorCatalogEntry,
  type CredentialVaultEntry,
  type IntegrationDashboard,
  type IntegrationNotification,
  type ProviderHealthStatus,
  type SyncJobRecord,
  type WebhookEndpoint,
} from '../../api/integration';

const WORKSPACE = (getWorkspaceId() ?? '');

const SUB_NAV = [
  { to: '/integrations/marketplace', label: 'Marketplace' },
  { to: '/integrations/gateway', label: 'API Gateway' },
  { to: '/integrations/webhooks', label: 'Webhooks' },
  { to: '/integrations/developer', label: 'Developer Portal' },
  { to: '/integrations/transformations', label: 'Transformations' },
  { to: '/integrations/health', label: 'Health' },
  { to: '/integrations/analytics', label: 'Analytics' },
  { to: '/integrations/logs', label: 'Logs' },
];

type Tab = 'overview' | 'marketplace' | 'connections' | 'sync' | 'webhooks' | 'vault' | 'health' | 'notifications';

export function IntegrationHubDashboardPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<IntegrationDashboard | null>(null);
  const [catalog, setCatalog] = useState<ConnectorCatalogEntry[]>([]);
  const [connections, setConnections] = useState<Connector[]>([]);
  const [syncJobs, setSyncJobs] = useState<SyncJobRecord[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [vault, setVault] = useState<CredentialVaultEntry[]>([]);
  const [health, setHealth] = useState<ProviderHealthStatus[]>([]);
  const [notifications, setNotifications] = useState<IntegrationNotification[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cat, conns, jobs, wh, creds, hlth, notifs, act] = await Promise.all([
        integrationApi.dashboard(WORKSPACE),
        integrationApi.catalog(categoryFilter || undefined, search || undefined),
        integrationApi.listConnections(WORKSPACE),
        integrationApi.listSyncJobs(WORKSPACE),
        integrationApi.webhooks(WORKSPACE),
        integrationApi.credentialVault(),
        integrationApi.health(WORKSPACE),
        integrationApi.listNotifications(true),
        integrationApi.listActivity(15),
      ]);
      setDashboard(dash);
      setCatalog(cat);
      setConnections(conns);
      setSyncJobs(jobs);
      setWebhooks(wh);
      setVault(creds);
      setHealth(hlth);
      setNotifications(notifs);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration hub');
    }
  }, [categoryFilter, search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connectAmazon = async () => {
    setBusy(true);
    try {
      const conn = await integrationApi.connectionWizard({
        workspace_id: WORKSPACE,
        catalog_id: 'amazon',
        name: 'Amazon Seller Central — Production',
      });
      await integrationApi.rotateCredential(conn.id, JSON.stringify({ type: 'oauth2', access_token: 'demo-token' }));
      await integrationApi.createSyncJob({
        workspace_id: WORKSPACE,
        connector_id: conn.id,
        strategy: 'incremental',
        domain: 'products',
      });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const runLatestSync = async () => {
    const pending = syncJobs.find((j) => j.status === 'queued' || j.status === 'running');
    if (pending) {
      await integrationApi.runSyncJob(pending.id);
      refresh();
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'marketplace', label: 'Connector Catalog' },
    { id: 'connections', label: 'Connections' },
    { id: 'sync', label: 'Sync Dashboard' },
    { id: 'webhooks', label: 'Webhooks' },
    { id: 'vault', label: 'Credential Vault' },
    { id: 'health', label: 'Health Monitor' },
    { id: 'notifications', label: 'Notifications' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Integration Hub</h1>
          <p className="text-sm text-slate-500">
            Enterprise API gateway, universal integration hub &amp; connector framework — DEV-000048
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={connectAmazon}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            Connect Amazon (Wizard)
          </button>
          <button type="button" onClick={refresh} className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <nav className="flex flex-wrap gap-3 text-sm">
        {SUB_NAV.map((l) => (
          <Link key={l.to} to={l.to} className="text-teal-700 hover:underline dark:text-teal-400">{l.label}</Link>
        ))}
      </nav>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              tab === t.id ? 'bg-teal-100 font-medium text-teal-800' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Catalog Connectors" value={dashboard.catalog_size} />
            <StatCard label="Active Connections" value={dashboard.active_connectors} />
            <StatCard label="Pending Sync Jobs" value={dashboard.pending_sync_jobs} />
            <StatCard label="Webhook Endpoints" value={dashboard.webhook_endpoints} />
            <StatCard label="Avg Latency (ms)" value={dashboard.avg_latency_ms.toFixed(0)} />
            <StatCard label="Events (24h)" value={dashboard.events_published_24h} />
            <StatCard label="Credentials Expiring" value={dashboard.credentials_expiring} />
            <StatCard label="Unread Alerts" value={dashboard.unread_notifications} />
          </div>
          <Panel title="Recent Activity">
            <ActivityList items={activity.length ? activity : dashboard.recent_activity ?? []} />
          </Panel>
        </div>
      )}

      {tab === 'marketplace' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All categories</option>
              {CONNECTOR_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search connectors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.map((e) => (
              <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <p className="font-medium text-slate-900">{e.name}</p>
                  {e.featured && <Badge text="featured" tone="teal" />}
                </div>
                <p className="mt-1 text-xs uppercase text-slate-400">{e.category} · {e.auth_type}</p>
                <p className="mt-2 text-sm text-slate-600">{e.description}</p>
                {e.supported_features && (
                  <p className="mt-2 text-xs text-slate-500">{e.supported_features.join(' · ')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'connections' && (
        <Panel title="Active Connections">
          {connections.length === 0 ? (
            <p className="text-sm text-slate-500">No connections yet. Use the wizard to connect a provider.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2">Name</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {connections.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="py-2 font-medium">{c.name}</td>
                    <td>{c.provider}</td>
                    <td><Badge text={c.status} tone={c.status === 'enabled' ? 'green' : 'blue'} /></td>
                    <td className="text-slate-500">{c.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === 'sync' && (
        <Panel title="Synchronization Jobs">
          <div className="mb-4">
            <button
              type="button"
              onClick={runLatestSync}
              disabled={!syncJobs.some((j) => j.status === 'queued')}
              className="rounded bg-teal-600 px-3 py-1 text-xs text-white hover:bg-teal-700 disabled:opacity-50"
            >
              Run Pending Sync
            </button>
          </div>
          {syncJobs.length === 0 ? (
            <p className="text-sm text-slate-500">No sync jobs scheduled.</p>
          ) : (
            <div className="space-y-2">
              {syncJobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm">
                  <div>
                    <span className="font-medium">{j.strategy}</span>
                    {j.domain && <span className="ml-2 text-slate-500">({j.domain})</span>}
                    {j.records_synced > 0 && <span className="ml-2 text-teal-700">{j.records_synced} records</span>}
                  </div>
                  <Badge text={j.status} tone={j.status === 'completed' ? 'green' : j.status === 'failed' ? 'red' : 'blue'} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {tab === 'webhooks' && (
        <Panel title="Webhook Manager">
          {webhooks.length === 0 ? (
            <p className="text-sm text-slate-500">No webhooks registered.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {webhooks.map((w) => (
                <li key={w.id} className="py-2">
                  <p className="font-medium">{w.url}</p>
                  <p className="text-xs text-slate-500">
                    {w.delivery_count} deliveries · {w.failure_count} failures
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {tab === 'vault' && (
        <Panel title="Credential Vault">
          {vault.length === 0 ? (
            <p className="text-sm text-slate-500">No credentials stored.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2">Connector</th>
                  <th>Auth</th>
                  <th>Version</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {vault.map((v) => (
                  <tr key={v.connector_id} className="border-b border-slate-100">
                    <td className="py-2">{v.connector_name ?? v.connector_id.slice(0, 8)}</td>
                    <td>{v.auth_type}</td>
                    <td>v{v.version}</td>
                    <td><Badge text={v.valid ? 'valid' : 'invalid'} tone={v.valid ? 'green' : 'red'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === 'health' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {health.length === 0 ? (
            <p className="text-sm text-slate-500">No health data. Connect a provider first.</p>
          ) : (
            health.map((h) => (
              <div key={h.connector_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="font-medium">{h.provider}</p>
                <Badge text={h.state} tone={h.state === 'healthy' ? 'green' : h.state === 'unhealthy' ? 'red' : 'amber'} />
                <p className="mt-2 text-xs text-slate-500">
                  Latency: {h.latency_ms?.toFixed(0) ?? '—'}ms · Error rate: {((h.error_rate ?? 0) * 100).toFixed(0)}%
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'notifications' && (
        <Panel title="Integration Alerts">
          {notifications.length === 0 ? (
            <p className="text-sm text-slate-500">No unread notifications.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <li key={n.id} className="py-3 text-sm">
                  <p className="font-medium">{n.title}</p>
                  {n.body && <p className="text-slate-600">{n.body}</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-medium text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Badge({ text, tone }: { text: string; tone: 'blue' | 'green' | 'amber' | 'red' | 'teal' }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
    teal: 'bg-teal-100 text-teal-800',
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${colors[tone]}`}>{text}</span>;
}

function ActivityList({ items }: { items: ActivityEntry[] }) {
  if (items.length === 0) return <p className="text-sm text-slate-500">No recent activity.</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((a) => (
        <li key={a.id} className="py-2 text-sm">
          <span className="font-medium">{a.action}</span> — {a.message}
          <p className="text-xs text-slate-400">{a.created_at}</p>
        </li>
      ))}
    </ul>
  );
}
